import type { Map, ExpressionSpecification, GeoJSONSource } from "maplibre-gl";

import type { GaugeFeature, GaugeFeatureCollection } from "../api.ts";

import {
  ALERT_COLORS,
  WELL_COLORS,
  RAIN_COLOR_DRY, RAIN_COLOR_LIGHT, RAIN_COLOR_MODERATE, RAIN_COLOR_HEAVY,
  RAIN_LIGHT, RAIN_MODERATE, RAIN_HEAVY,
  RIVER_UNMONITORED_LINE, RIVER_UNMONITORED_FILL,
  FLOOD_CURRENT_FILL, FLOOD_CURRENT_LINE, FLOOD_HISTORIC_FILL, FLOOD_HISTORIC_LINE,
} from "./map-colors.ts";

// ── Layer helpers ─────────────────────────────────────────────────────────────

function attachClickHandlers(
  map: Map,
  layerId: string,
  onClick: (feature: GaugeFeature) => void,
): void {
  map.on("click", layerId, (e) => {
    const feature = e.features?.[0];
    if (!feature) return;
    onClick(feature as unknown as GaugeFeature);
  });
  map.on("mouseenter", layerId, () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", layerId, () => { map.getCanvas().style.cursor = ""; });
}

// ── Rainfall gauges ───────────────────────────────────────────────────────────

export function addRainfallLayer(
  map: Map,
  geojson: GaugeFeatureCollection,
  onClick: (feature: GaugeFeature) => void,
): void {
  const sourceId = "rainfall-gauges";
  // Dual-gauge sites are rendered as HTML markers — exclude them from the symbol layer
  const data: GaugeFeatureCollection = {
    ...geojson,
    features: geojson.features.filter((f) => !f.properties.multi_gauge),
  };

  if (map.getSource(sourceId)) {
    (map.getSource(sourceId) as GeoJSONSource).setData(data);
    return;
  }

  map.addSource(sourceId, { type: "geojson", data });
  map.addLayer({
    id: "rainfall-symbols",
    type: "symbol",
    source: sourceId,
    layout: {
      "icon-image": [
        "step", ["get", "value"],
        "rainfall-dry",
        RAIN_LIGHT, "rainfall-light",
        RAIN_MODERATE, "rainfall-moderate",
        RAIN_HEAVY, "rainfall-heavy",
      ] as ExpressionSpecification,
      "icon-size": 0.84,
      "icon-allow-overlap": true,
    },
  });

  // Trend arrow SDF icons — to the right of each rainfall icon
  map.addLayer({
    id: "rainfall-trends",
    type: "symbol",
    source: sourceId,
    layout: {
      "icon-image": [
        "match", ["get", "trend"],
        "increasing", "trend-up",
        "decreasing", "trend-down",
        "stable",     "trend-stable",
        "",
      ] as ExpressionSpecification,
      "icon-size": 1,
      "icon-anchor": "left",
      "icon-offset": ["literal", [22, 0]] as ExpressionSpecification,
      "icon-allow-overlap": true,
    },
    paint: {
      "icon-color": [
        "step", ["get", "value"],
        RAIN_COLOR_DRY,
        RAIN_LIGHT, RAIN_COLOR_LIGHT,
        RAIN_MODERATE, RAIN_COLOR_MODERATE,
        RAIN_HEAVY, RAIN_COLOR_HEAVY,
      ] as ExpressionSpecification,
    },
  });

  attachClickHandlers(map, "rainfall-symbols", onClick);
}

// ── River polygon fills ───────────────────────────────────────────────────────
// Filled polygon features from LINZ layer 103631 (NZ River Name Polygons).
// alert_level is stamped per polygon by colorRiverPolygons() at render time.

export function addRiverPolygonsLayer(
  map: Map,
  colored: GeoJSON.FeatureCollection,
): void {
  const sourceId = "river-polygons";

  if (map.getSource(sourceId)) {
    (map.getSource(sourceId) as GeoJSONSource).setData(colored);
    return;
  }

  map.addSource(sourceId, { type: "geojson", data: colored });

  // Shared colour expression for borders and fill-outline — alert level drives
  // colour; unmonitored reaches (null alert_level) fall back to steel blue.
  const alertLineColorExpr: ExpressionSpecification = [
    "match", ["get", "alert_level"],
    "high",   ALERT_COLORS.high!,
    "watch",  ALERT_COLORS.watch!,
    "normal", ALERT_COLORS.normal!,
    RIVER_UNMONITORED_LINE,
  ];

  const opacityExpr: ExpressionSpecification = [
    "case", ["!=", ["get", "alert_level"], null], 0.85, 0.45,
  ];

  // Border layer sits beneath the fill — widens monitored rivers at low zoom.
  map.addLayer({
    id: "river-polygon-borders",
    type: "line",
    source: sourceId,
    paint: {
      "line-color": alertLineColorExpr,
      "line-width": [
        "interpolate", ["linear"], ["zoom"],
        5, 8,
        10, 4,
        14, 2,
      ] as ExpressionSpecification,
      "line-opacity": opacityExpr,
    },
  });

  map.addLayer({
    id: "river-polygons",
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": [
        "match", ["get", "alert_level"],
        "high",   ALERT_COLORS.high!,
        "watch",  ALERT_COLORS.watch!,
        "normal", ALERT_COLORS.normal!,
        RIVER_UNMONITORED_FILL, // vivid sky blue for reaches with no gauge data
      ] as ExpressionSpecification,
      "fill-opacity": opacityExpr,
      "fill-outline-color": alertLineColorExpr,
    },
  });
}

// ── Flood extent observations ─────────────────────────────────────────────────
// HBRC flood extent polygons from ArcGIS FeatureServer. Lazy-loaded on first
// toggle. Status field drives styling: "Current" (blue) vs "Historic" (pale).

export function addFloodExtentsLayer(
  map: Map,
  geojson: GeoJSON.FeatureCollection,
): void {
  const sourceId = "flood-extents";

  if (map.getSource(sourceId)) {
    (map.getSource(sourceId) as GeoJSONSource).setData(geojson);
    return;
  }

  map.addSource(sourceId, { type: "geojson", data: geojson });

  // Insert below river polygon layers so gauge markers remain on top.
  const beforeLayer = map.getLayer("river-polygon-borders") ? "river-polygon-borders" : undefined;

  map.addLayer(
    {
      id: "flood-extents-fill",
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": [
          "match", ["get", "Status"],
          "Current", FLOOD_CURRENT_FILL,
          FLOOD_HISTORIC_FILL,
        ] as ExpressionSpecification,
        "fill-opacity": [
          "match", ["get", "Status"],
          "Current", 0.4,
          0.2,
        ] as ExpressionSpecification,
      },
    },
    beforeLayer,
  );

  map.addLayer(
    {
      id: "flood-extents-border",
      type: "line",
      source: sourceId,
      paint: {
        "line-color": [
          "match", ["get", "Status"],
          "Current", FLOOD_CURRENT_LINE,
          FLOOD_HISTORIC_LINE,
        ] as ExpressionSpecification,
        "line-opacity": [
          "match", ["get", "Status"],
          "Current", 0.8,
          0.5,
        ] as ExpressionSpecification,
        "line-width": 1,
      },
    },
    beforeLayer,
  );
}

// ── River level gauges ────────────────────────────────────────────────────────

export function addRiverLayer(
  map: Map,
  geojson: GaugeFeatureCollection,
  onClick: (feature: GaugeFeature) => void,
): void {
  const sourceId = "river-gauges";
  const data: GaugeFeatureCollection = {
    ...geojson,
    features: geojson.features.filter((f) => !f.properties.multi_gauge),
  };

  if (map.getSource(sourceId)) {
    (map.getSource(sourceId) as GeoJSONSource).setData(data);
    return;
  }

  map.addSource(sourceId, { type: "geojson", data });
  map.addLayer({
    id: "river-symbols",
    type: "symbol",
    source: sourceId,
    layout: {
      "icon-image": [
        "match", ["get", "alert_level"],
        "high",  "river-icon-high",
        "watch", "river-icon-watch",
        "river-icon-normal",
      ] as ExpressionSpecification,
      "icon-size": 0.55,
      "icon-allow-overlap": true,
    },
  });

  attachClickHandlers(map, "river-symbols", onClick);
}

// ── Well gauges ───────────────────────────────────────────────────────────────

export function addWellLayer(
  map: Map,
  geojson: GaugeFeatureCollection,
  onClick: (feature: GaugeFeature) => void,
): void {
  const sourceId = "well-gauges";

  if (map.getSource(sourceId)) {
    (map.getSource(sourceId) as GeoJSONSource).setData(geojson);
    return;
  }

  map.addSource(sourceId, { type: "geojson", data: geojson });
  map.addLayer({
    id: "well-symbols",
    type: "symbol",
    source: sourceId,
    layout: {
      "icon-image": "well-icon",
      "icon-size": 0.85,
      "icon-allow-overlap": true,
    },
    paint: {
      "icon-color": [
        "case",
        ["==", ["get", "stale"], true],                    WELL_COLORS.stale.icon,
        ["==", ["get", "groundwater_level"], "high"],       WELL_COLORS.high.icon,
        ["==", ["get", "groundwater_level"], "normal"],     WELL_COLORS.normal.icon,
        ["==", ["get", "groundwater_level"], "low"],        WELL_COLORS.low.icon,
        WELL_COLORS.lowData.icon,
      ],
      "icon-halo-color": [
        "case",
        ["==", ["get", "stale"], true],                    WELL_COLORS.stale.halo,
        ["==", ["get", "groundwater_level"], "high"],       WELL_COLORS.high.halo,
        ["==", ["get", "groundwater_level"], "normal"],     WELL_COLORS.normal.halo,
        ["==", ["get", "groundwater_level"], "low"],        WELL_COLORS.low.halo,
        WELL_COLORS.lowData.halo,
      ],
      "icon-halo-width": 3,
      "icon-halo-blur": 2,
    },
  });

  attachClickHandlers(map, "well-symbols", onClick);
}
